import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ISubcode, ICodeSeqReq } from '../../../../models/codes.model';
import { doGetSubcodes, doUpdateSubcodeSeq } from '../../../../store/code.slice';
import { doRemoveAuth } from '../../../../store/auth.slice';
import { RootState } from '../../../../store/rootReducer';
import qs from 'qs';
import _ from 'lodash';

import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';


import Loader from '../../../../components/Loader';
import SimpleHeader from '../../../../components/layout/SimpleHeader';
import Footer from '../../../../components/layout/Footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import { errorVariant, successVariant, warningVariant } from '../../../../lib/barVariant';
import useAuth from '../../../../hooks/useAuth';
const { Option } = Select;

interface IMatch {
  id: string;
}

const Block = styled.div`
  .button {
    .disable > img {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const SystemCodeSubcode: FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector((state: RootState) => state.auth);
  const { subcodes, isLoading } = useSelector((state: RootState) => state.code);

  const match = useRouteMatch<IMatch>();
  const id = String(match.params.id);
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [codeData, setCodeData] = useState<ISubcode[]>([]);
  const [codeCount, setCodeCount] = useState(0);
  const [field, setField] = useState<any>();
  const [keyword, setKeyword] = useState('');
  const [isInUse, setIsInUse] = useState(false);
  const [topCodeName, setTopCodeName] = useState({ ko: '', en: '' });

  const fieldList = [
    { label: t('system.code.column2'), value: 'korName' },
    { label: t('system.code.column3'), value: 'engName' }
  ];

  useEffect(() => {
    if (i18n.language === 'ko') {
      setField('korName');
    } else {
      setField('engName');
    }

    getCodes();
  }, [])

  useEffect(() => {
    // query to state
    if (query.subField) {
      setField(String(query.subField));
    }
    if (query.subKeyword) {
      setKeyword(String(query.subKeyword));
    }
    if (query.subUse) {
      setIsInUse(query.subUse === 'true');
    }
    if (query.ko) {
      setTopCodeName({
        ko: String(query.ko) || '',
        en: String(query.en) || ''
      })
    }

    // data filtering
    if (subcodes) {
      let data: ISubcode[] = _.cloneDeep(subcodes);
      data = sortCode(data);

      if (query.subField && query.subKeyword) {
        data = data.filter(code => code[String(query.subField)].indexOf(String(query.subKeyword)) !== -1);
      }
      if (query.subUse) {
        data = data.filter(code => code['useYn'] === 'Y');
      }

      setCodeCount(data.length);
      setCodeData(data);
    }
  }, [location, subcodes]);

  const getCodes = async () => {
    const res = await dispatch(doGetSubcodes(id));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  };

  const onSearch = () => {
    history.push(`/system/code/${id}/subcode?${qs.stringify({
      ...query,
      subField: field,
      subKeyword: keyword,
      subUse: isInUse ? 'true' : '',
    })}`);
  };

  const sortCode = (menus: ISubcode[]) => menus.sort((a, b) => a.orderSeq < b.orderSeq ? -1 : (a.orderSeq > b.orderSeq ? 1 : 0));

  const raiseMenu = (orderSeq: number) => {
    const _code = [...codeData];
    const index = _code.findIndex(code => code.orderSeq === orderSeq);
    _code[index].orderSeq--;
    _code[index - 1].orderSeq++;
    setCodeData(sortCode(_code));
  }

  const lowerMenu = (orderSeq: number) => {
    const _code = [...codeData];
    const index = _code.findIndex(code => code.orderSeq === orderSeq);
    _code[index].orderSeq++;
    _code[index + 1].orderSeq--;
    setCodeData(sortCode(_code));
  }

  const saveSequence = async () => {
    const seqData: ICodeSeqReq[] = [];
    codeData.forEach(code => {
      seqData.push({
        detailCode: code.detailCode,
        orderSeq: code.orderSeq
      })
    })

    const res = await dispatch(doUpdateSubcodeSeq(id, seqData));

    switch (res?.status) {
      case 200:
        enqueueSnackbar(t('common.message.save'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const columns: TableColumnsType<ISubcode> = [
    {
      title: t('system.menu.column1'),
      dataIndex: 'orderSeq',
      key: 'orderSeq',
      sorter: (a, b) => a.orderSeq - b.orderSeq
    },
    {
      title: t('system.code.column1'),
      dataIndex: 'detailCode',
      key: 'detailCode',
      sorter: (a, b) => a.detailCode?.localeCompare(b.detailCode)
    },
    {
      title: t('system.code.column2'),
      dataIndex: 'korName',
      key: 'korName',
      sorter: (a, b) => a.korName?.localeCompare(b.korName)
    },
    {
      title: t('system.code.column3'),
      dataIndex: 'engName',
      key: 'engName',
      sorter: (a, b) => a.engName?.localeCompare(b.engName)
    },
    {
      title: t('system.menu.column4'),
      dataIndex: 'orderSeq',
      key: 'orderSeq',
      render: (value: number) => (
        <div className="order-sett__area">
          <a
            className={value === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (value === 1) return;
              raiseMenu(value);
            }}
          />
          <a
            className={value === codeData.length ? 'btn-order down disabled' : 'btn-order down'}
            onClick={() => {
              if (value === codeData.length) return;
              lowerMenu(value);
            }}
          />
        </div>
      ),
    },
    {
      title: t('system.code.column4'),
      dataIndex: 'useYn',
      key: 'useYn',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn)
    },
    {
      title: '',
      key: 'detailCode',
      dataIndex: 'detailCode',
      render: (detailCode: string) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className='btn-basic small pink-line'
            onClick={() => history.push(`/system/code/${id}/subcode/${detailCode}/edit${location.search}`)}
            text={t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    }
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={`${t('system.code.title1')} (${i18n.language === 'ko' ? topCodeName.ko : topCodeName.en })`}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('system.code.column5')}</label>
            <div className="w-150p mr-1">
              <Select
                className="w-100"
                value={field}
                onChange={value => setField(value)}
              >
                {
                  fieldList?.map((item: any) => {
                    return (
                      <Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Option>
                    )
                  })
                }
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              value={keyword}
              onChange={(e => setKeyword(e.target.value))}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
            <div className='comm-btn__area'>
              <Button
                className='btn-basic small black'
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
          <li>
            <label>{t('system.code.column4')}</label>
            <div className="text-left">
              <Checkbox
                checked={isInUse}
                onChange={setIsInUse}
                text={t('common.text.inUse')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <Block className="content scrollbar">
      {isLoading ? <Loader /> : null}

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{codeCount}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/system/code/${id}/subcode/create${location.search}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={codeData}
            pagination={{
              pageSize: 99999,
            }}
          />
        </div>
        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className='btn-basic gray-line'
              onClick={() => {
                history.push(
                  `/system/code?${qs.stringify({
                    field: query.field,
                    keyword: query.keyword,
                  })}`
                );
              }}
              text={t('system.code.button2')}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={saveSequence}
              text={t('system.menu.button2')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />
    </Block>
    </>
  )
}

export default SystemCodeSubcode;
